import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import { createAdminApi, type AdminRole, type AdminUserDetail } from '@/admin/adminApi';
import { ConsentsPanel, DeletionRequestPanel } from '@/admin/privacy/AccountPrivacyPanels';
import { AccountSensitiveActions } from '@/admin/privacy/AccountSensitiveActions';
import { useRefreshOnFocus } from '@/admin/useRefreshOnFocus';
import { useHttpClient } from '@/app/providers';
import { Avatar, Button, ErrorState, LoadingState, Tabs, type TabItem } from '@/ui/kit';

import {
  ABSENT,
  accountStates,
  describeSessionCount,
  formatInstant,
  suspensionReasonLabel,
} from './accountPresentation';
import { isNotFound, messageOf } from './adminErrors';
import { BabiesPanel, JournalPanel } from './AccountFamilyJournalPanels';
import { SyncCard, ActivityPanel, SessionsPanel, useAccountActivity } from './AccountUsagePanels';
import { AccessPanel, Fact, Facts, StateChips, TechnicalId } from './accountsUi';
import { useAdminAccess } from './useAdminAccess';

/**
 * Fiche 360° d'un compte, ouverte dans le volet de la liste (maquette P05).
 *
 * Elle repond, dans cet ordre, aux questions que pose un exploitant qui a
 * quelqu'un au telephone : **qui est-ce**, **a quels enfants est-il rattache**,
 * **est-il connecte quelque part**, **qu'a-t-il accepte**, **que s'est-il
 * passe** — et seulement ensuite, **que puis-je faire pour lui**.
 *
 * Trois points de conception a ne pas defaire :
 *
 * - **Une requete pour le resume**, `GET /admin/users/{id}`, qui agrege les
 *   sources du compte ; les lectures lourdes (activite, sessions, journal) sont
 *   des requetes a part, chacune avec son etat : une route en panne ne prive pas
 *   l'ecran du reste. Les sessions et le journal ne sont lus que quand leur
 *   onglet est ouvert.
 * - **Un echec n'efface pas ce qui etait lu.** La derniere fiche obtenue reste
 *   affichee sous le message d'erreur : un 403 survenu apres coup — un role
 *   retire pendant la consultation — ne doit pas faire disparaitre l'ecran, et
 *   ne deconnecte personne.
 * - **Rien ne se relit seul** (ADR 0035 §4) : chaque lecture `/admin/*` ecrit une
 *   ligne d'audit. « Actualiser » et le retour sur l'onglet relisent tout.
 */

interface DetailState {
  readonly busy: boolean;
  readonly detail: AdminUserDetail | null;
  readonly error: string | null;
  readonly missing: boolean;
}

/**
 * Issue d'un chargement, rangee avec la **clef** de la demande qui l'a produite.
 * L'attente est deduite de la comparaison des clefs, jamais ecrite par un
 * `setState` dans l'effet — qui couterait un rendu de plus a chaque passage et
 * que React deconseille explicitement.
 */
interface LoadOutcome {
  readonly key: string;
  readonly detail: AdminUserDetail | null;
  readonly error: string | null;
  readonly missing: boolean;
}

const useAccountDetail = ({
  enabled,
  userId,
  attempt,
}: {
  enabled: boolean;
  userId: string;
  attempt: number;
}): DetailState => {
  const http = useHttpClient();
  const [outcome, setOutcome] = useState<LoadOutcome | null>(null);
  const key = `${userId}|${String(attempt)}`;

  useEffect(() => {
    if (!enabled || userId === '') {
      return;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const detail = await createAdminApi(http).getUser(userId);

        if (!cancelled) {
          setOutcome({ key, detail, error: null, missing: false });
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        setOutcome((previous) => ({
          key,
          // La fiche deja lue reste affichee : un echec de relecture ne doit pas
          // vider l'ecran sous les yeux de qui le consultait.
          detail: previous?.detail ?? null,
          // Un compte inconnu n'est pas une panne : il merite sa propre phrase,
          // pas un « Réessayer » qui echouera autant de fois qu'on le clique.
          missing: isNotFound(error),
          error: isNotFound(error) ? null : messageOf(error),
        }));
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [http, enabled, userId, attempt, key]);

  const current = outcome?.key === key ? outcome : null;

  return {
    busy: enabled && userId !== '' && current === null,
    detail: outcome?.detail ?? null,
    error: current?.error ?? null,
    missing: current?.missing ?? false,
  };
};

// ---------------------------------------------------------------------------
// Identite
// ---------------------------------------------------------------------------

const IdentityHeader = ({
  detail,
  busy,
  onRefresh,
}: {
  detail: AdminUserDetail;
  busy: boolean;
  onRefresh: () => void;
}): ReactElement => (
  <Box component="header" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Avatar name={detail.email} size={48} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="h2" sx={{ fontSize: 20, lineHeight: '26px', fontWeight: 800, wordBreak: 'break-all' }}>
          {detail.email}
        </Typography>
        <TechnicalId value={detail.id} label="l’identifiant du compte" />
      </Box>
      <Button size="S" icon={RefreshCw} onClick={onRefresh} disabled={busy}>
        Actualiser
      </Button>
    </Box>

    <StateChips states={accountStates(detail)} />

    {detail.suspendedAt === null ? null : (
      <Alert severity="warning" variant="outlined">
        Compte suspendu depuis le {formatInstant(detail.suspendedAt)} — motif :{' '}
        {suspensionReasonLabel(detail.suspensionReasonCode)}. La personne ne peut plus se connecter ; ses données et
        ses partages sont conservés.
      </Alert>
    )}

    {detail.anonymized ? (
      <Alert severity="info" variant="outlined">
        <AlertTitle>Compte anonymisé : lecture seule</AlertTitle>
        L’adresse, le nom et la photo ont été remplacés. Le serveur n’expose plus ni profils, ni sessions, ni activité
        pour ce compte, et aucune action n’est proposée.
      </Alert>
    ) : null}

    <Facts>
      <Fact label="Compte créé le">{formatInstant(detail.createdAt)}</Fact>
      <Fact label="Dernière connexion">{formatInstant(detail.lastLoginAt, 'Jamais connecté')}</Fact>
      <Fact label="Adresse vérifiée le">{formatInstant(detail.emailVerifiedAt, 'Adresse jamais vérifiée')}</Fact>
      <Fact label="Appareils connectés">{describeSessionCount(detail.activeSessionCount)}</Fact>
    </Facts>
  </Box>
);

// ---------------------------------------------------------------------------
// Onglets
// ---------------------------------------------------------------------------

type TabKey = 'overview' | 'families' | 'devices' | 'consents' | 'activity' | 'journal';

const PANEL_ID = 'account-panel';

const AccountBody = ({
  detail,
  role,
  operatorEmail,
  busy,
  refreshToken,
  onRefresh,
}: {
  detail: AdminUserDetail;
  role: AdminRole;
  operatorEmail: string;
  busy: boolean;
  refreshToken: number;
  onRefresh: () => void;
}): ReactElement => {
  const [tab, setTab] = useState<TabKey>('overview');
  const activity = useAccountActivity(detail.id, refreshToken, !detail.anonymized);

  // Les compteurs d'onglet sont ceux de la reponse : le nombre de profils rendus
  // et le nombre de sessions actives. Un compte anonymise n'en expose aucun.
  const items: readonly TabItem<TabKey>[] = [
    { value: 'overview', label: 'Aperçu' },
    detail.anonymized
      ? { value: 'families', label: 'Familles et bébés' }
      : { value: 'families', label: 'Familles et bébés', count: detail.babies.length },
    detail.anonymized
      ? { value: 'devices', label: 'Appareils et sessions' }
      : { value: 'devices', label: 'Appareils et sessions', count: detail.activeSessionCount },
    { value: 'consents', label: 'Consentements' },
    { value: 'activity', label: 'Activité' },
    { value: 'journal', label: 'Journal du compte' },
  ];

  return (
    <>
      <IdentityHeader detail={detail} busy={busy} onRefresh={onRefresh} />

      <Tabs label="Sections de la fiche" value={tab} onChange={setTab} items={items} panelId={PANEL_ID} />

      <Box id={PANEL_ID} role="tabpanel" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {tab === 'overview' ? (
          <>
            <SyncCard
              userId={detail.id}
              activity={activity.state}
              onRetry={activity.reload}
              activeSessionCount={detail.activeSessionCount}
              anonymized={detail.anonymized}
            />
            <DeletionRequestPanel request={detail.latestDeletionRequest} />
          </>
        ) : null}
        {tab === 'consents' ? <ConsentsPanel consents={detail.consents} /> : null}
        {tab === 'activity' ? (
          <ActivityPanel activity={activity.state} onRetry={activity.reload} anonymized={detail.anonymized} />
        ) : null}
        {tab === 'devices' ? (
          <SessionsPanel userId={detail.id} refreshToken={refreshToken} anonymized={detail.anonymized} />
        ) : null}
        {tab === 'families' ? <BabiesPanel babies={detail.babies} anonymized={detail.anonymized} /> : null}
        {tab === 'journal' ? <JournalPanel userId={detail.id} refreshToken={refreshToken} /> : null}
      </Box>

      <AccountSensitiveActions user={detail} role={role} operatorEmail={operatorEmail} onChanged={onRefresh} />
    </>
  );
};

export const AccountDetailScreen = ({ userId }: { userId: string }): ReactElement => {
  const { access, reload: reloadAccess } = useAdminAccess();
  const granted = access.kind === 'granted';
  const [refreshToken, setRefreshToken] = useState(0);

  const { busy, detail, error, missing } = useAccountDetail({ enabled: granted, userId, attempt: refreshToken });

  const refresh = (): void => {
    setRefreshToken((previous) => previous + 1);
  };

  // Seule relecture automatique (ADR 0035 §4) : au retour sur l'onglet.
  useRefreshOnFocus(refresh);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <AccessPanel access={access} onRetry={reloadAccess} />

      {granted && missing ? (
        <Alert severity="warning" variant="outlined">
          <AlertTitle>Compte introuvable</AlertTitle>
          Aucun compte ne porte l’identifiant {userId === '' ? ABSENT : userId}. Il a pu être saisi de travers, ou le
          lien provient d’un écran plus ancien.
        </Alert>
      ) : null}

      {error === null ? null : <ErrorState message={error} onRetry={refresh} />}

      {granted && detail === null && busy ? <LoadingState label="Chargement de la fiche…" /> : null}

      {detail === null || access.kind !== 'granted' ? null : (
        <AccountBody
          detail={detail}
          role={access.role}
          operatorEmail={access.identity.email}
          busy={busy}
          refreshToken={refreshToken}
          onRefresh={refresh}
        />
      )}
    </Box>
  );
};
